/**
 * Web Push — клиентская обвязка (Phase 4.6 / hh-711).
 *
 * registerServiceWorker() регистрирует /sw.js (push-обработчик + offline-кэш).
 * enablePushNotifications() спрашивает разрешение, подписывается через
 * PushManager с VAPID-ключом сервера и шлёт подписку на /api/v1/push/subscribe.
 */
import { push } from '../../api';

/** Итог попытки включить push-уведомления. */
export type PushSetupResult =
  | 'enabled'
  | 'unsupported'
  | 'denied'
  | 'server-disabled'
  | 'error';

/** VAPID-ключ приходит base64url-строкой — PushManager ждёт Uint8Array. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Браузер поддерживает service worker + push. */
export function isPushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Регистрирует service worker. Тихо игнорирует отсутствие поддержки. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch {
    return null;
  }
}

/**
 * Включает push-уведомления: разрешение → подписка PushManager → отправка
 * подписки на сервер. Идемпотентно — повторный вызов переиспользует
 * существующую подписку.
 */
export async function enablePushNotifications(): Promise<PushSetupResult> {
  if (!isPushSupported()) return 'unsupported';

  try {
    const vapid = await push.getVapidKey();
    if (!vapid.enabled || vapid.publicKey === null) return 'server-disabled';

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return 'denied';

    const registration =
      (await navigator.serviceWorker.getRegistration()) ?? (await registerServiceWorker());
    if (registration === null) return 'error';
    await navigator.serviceWorker.ready;

    let subscription = await registration.pushManager.getSubscription();
    if (subscription === null) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.publicKey),
      });
    }

    const json = subscription.toJSON();
    if (
      json.endpoint === undefined ||
      json.keys?.p256dh === undefined ||
      json.keys?.auth === undefined
    ) {
      return 'error';
    }

    await push.subscribePush({
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    });
    return 'enabled';
  } catch {
    return 'error';
  }
}
